/**
 * Os avisos não cobrem as ações do diálogo (rodada de correção da v1.8.0, FX2 · DS-654 (t)).
 *
 * Pura de propósito, sem React e sem DOM: a suíte de unidade do web roda em node, e é esta
 * função, e não a pilha (`toast.tsx`), que decide quanto os avisos sobem. A pilha só mede as
 * caixas e aplica a resposta.
 *
 * ## O defeito que ela fecha
 *
 * A pilha de toasts mora no canto de baixo à direita, por cima do diálogo (ela é portada para
 * DENTRO dele, `useDestinoDosAvisos`). No celular o diálogo ocupa a largura da tela, o cartão do
 * toast também (de 260 a 360 px, a 16 px da borda), e o rodapé do diálogo alto fica no chão da
 * tela: o aviso deita em cima do "Salvar". O cartão recebe o clique (`pointer-events-auto`, para o
 * "Desfazer" e o "Fechar" funcionarem), e o ponteiro parado sobre ele CONGELA os seis segundos
 * (lote U1, A11Y-05): o toast que nasce debaixo do dedo que acabou de tocar o "Salvar" fica ali, e
 * o segundo toque cai no aviso. Medido na Composição da 5.2 a 412 px: o toast de 102 px de altura
 * cobria o "Salvar composição" de 721 a 751 px.
 *
 * A regra é a mesma do rodapé fixo das telas de formulário (`useRodapeFixo`): quem cede espaço é a
 * camada que não é ato. Subir a ação por cima do aviso deixaria o aviso inalcançável.
 */

/** Uma caixa na tela, em px, na forma que `getBoundingClientRect` devolve. */
export interface Caixa {
  readonly top: number
  readonly bottom: number
  readonly left: number
  readonly right: number
}

/**
 * A marca do rodapé cujos controles os avisos não cobrem. Quem a põe é o `Modal` que pede
 * (`protegerRodape`); diálogo sem a marca mantém a pilha onde sempre esteve.
 */
export const MARCA_DAS_ACOES_DO_DIALOGO = 'data-dv-acoes-do-dialogo'

/** O que conta como AÇÃO no rodapé: o controle que a pessoa aciona, e não a nota ao lado dele. */
export const SELETOR_DAS_ACOES = 'a[href], button, input, select, textarea, [role="button"]'

/** O vão entre o cartão erguido e o controle, para a sombra do cartão não encostar no botão. */
export const FOLGA_SOBRE_AS_ACOES = 8

function temArea(c: Caixa): boolean {
  return c.right > c.left && c.bottom > c.top
}

function cruzam(a: Caixa, b: Caixa): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top
}

/**
 * Quantos px a pilha de avisos sobe para não cobrir nenhum controle do rodapé do diálogo.
 *
 * - `pilha`: a caixa da pilha medida AGORA, já com a elevação `elevacaoAtual` aplicada. A conta
 *   desfaz a elevação para julgar a pilha no lugar dela: julgada já erguida, ela deixaria de cruzar
 *   o botão, cairia de volta e subiria de novo no quadro seguinte.
 * - `acoes`: as caixas dos controles do rodapé.
 * - `visivel`: a região em que um controle aparece (a tela recortada pelo painel que rola). O
 *   controle que rolou para fora do painel não está na tela, e não empurra aviso nenhum.
 *
 * Os controles são vistos de baixo para cima: cada um que a pilha cruza a empurra para acima
 * dele, e o de cima ainda pode ser cruzado pela pilha já empurrada (o rodapé que quebra em duas
 * linhas no celular). Nenhum controle cruzado: zero, e a pilha fica no chão.
 */
export function elevacaoSobreAsAcoes(pilha: Caixa, elevacaoAtual: number, acoes: readonly Caixa[], visivel: Caixa): number {
  const chao: Caixa = { top: pilha.top + elevacaoAtual, bottom: pilha.bottom + elevacaoAtual, left: pilha.left, right: pilha.right }
  if (!temArea(chao)) return 0
  const altura = chao.bottom - chao.top
  const naTela = acoes.filter((a) => temArea(a) && cruzam(a, visivel)).sort((a, b) => b.top - a.top)
  let fundo = chao.bottom
  for (const acao of naTela) {
    if (cruzam(acao, { top: fundo - altura, bottom: fundo, left: chao.left, right: chao.right })) fundo = acao.top - FOLGA_SOBRE_AS_ACOES
  }
  return fundo >= chao.bottom ? 0 : Math.ceil(chao.bottom - fundo)
}
